import {
  LOWEST_CARD, HIGHEST_CARD, CARDS_REMOVED, MIN_PLAYERS, MAX_PLAYERS,
  countersForPlayerCount, PHASES, clampRounds,
} from '../constants';
import { createRngState, shuffle, nextInt } from '../rng/rng';

/**
 * The full 33-card deck, 3..35, in order. Shuffled by createGameState.
 */
export const fullDeck = () => {
  const cards = [];
  for (let value = LOWEST_CARD; value <= HIGHEST_CARD; value += 1) cards.push(value);
  return cards;
};

/**
 * Shuffles all 33, drops nine unseen, and flips the top card. Shared by the
 * first deal and every later round of a match, so a new round is set up by
 * exactly the same rule (p1, Set Up 1-2) as the first one.
 */
export const dealRound = (rngState) => {
  const [shuffled, afterShuffle] = shuffle(fullDeck(), rngState);
  // Nine cards leave the game, unseen and unrecorded.
  const deck = shuffled.slice(CARDS_REMOVED);
  // The top card is flipped immediately - the first player's turn begins with
  // a card already face up, since their turn IS "flip and decide" (p2, 1a).
  const currentCard = deck.pop();
  return { deck, currentCard, rngState: afterShuffle };
};

/**
 * Builds a new game.
 *
 * `players` is an array of { name, isBot } - 3 to 7 of them (p1 header).
 *
 * Setup, in rulebook order (p1, Set Up):
 *   1. Shuffle all 33 and put nine back in the box unseen.
 *   2. The rest form the face-down stack.
 *   3. Each player gets a counter supply sized by the player count.
 *
 * The nine removed cards are dropped on the floor here, not stashed in state.
 * That is deliberate and load-bearing: "without looking at them" is the rule,
 * and anything the state carries can leak into a devtools panel, a saved game,
 * or a log line. The only thing kept is how many were removed, which is public.
 *
 * `startingPlayerIndex` defaults to a seeded random pick. The rulebook says
 * "If you're the oldest player, you start play" (p2), which a digital version
 * has no way to know and no business asking, so the seat is drawn instead -
 * from the same RNG, so a seed still reproduces the whole game exactly.
 *
 * `rounds` is the house-rule match length (constants.js, MAX_ROUNDS). One is
 * the rulebook game. A state saved before rounds existed has no
 * `meta.rounds`, and everything that reads it treats that as 1.
 */
export const createGameState = ({
  players, seed = Date.now(), startingPlayerIndex = null, rounds = 1,
} = {}) => {
  if (!Array.isArray(players) || players.length < MIN_PLAYERS || players.length > MAX_PLAYERS) {
    throw new Error(`No Thanks! needs ${MIN_PLAYERS}-${MAX_PLAYERS} players, got ${players?.length ?? 0}`);
  }

  const dealt = dealRound(createRngState(seed));
  const { deck, currentCard } = dealt;
  let { rngState } = dealt;

  let firstSeat = startingPlayerIndex;
  if (firstSeat === null || firstSeat === undefined) {
    const [pick, afterPick] = nextInt(players.length, rngState);
    rngState = afterPick;
    firstSeat = pick;
  }

  const counters = countersForPlayerCount(players.length);

  return {
    meta: {
      phase: PHASES.PLAYING,
      activePlayerIndex: firstSeat,
      startingPlayerIndex: firstSeat,
      seed,
      rngState,
      cardsRemoved: CARDS_REMOVED,
      turn: 1,
      // Monotonic action counter. Local play never reads it; online play
      // writes state back only if the copy it started from is still the
      // current one, so two clients can't both apply the same turn (see
      // src/store/multiplayer.js).
      seq: 0,
      round: 1,
      rounds: clampRounds(rounds),
    },
    // One entry per finished round: { round, scores: { [playerId]: score } }.
    roundResults: [],
    deck,
    currentCard,
    countersOnCard: 0,
    players: players.map((player, index) => ({
      id: player.id ?? `p${index}`,
      name: player.name || `Player ${index + 1}`,
      isBot: Boolean(player.isBot),
      seat: index,
      counters,
      cards: [],
    })),
    log: [{
      kind: 'gameStart',
      playerCount: players.length,
      counters,
      firstPlayer: players[firstSeat]?.name,
      card: currentCard,
    }],
  };
};
